import type { CSSProperties } from 'react';
import { DepotVocal } from './depot-vocal.js';
import { PREMIER_CONTACT } from '../../api/contrat.js';
import { Lien } from '../../app/navigation.js';
import { CIBLE_MIN, COULEURS, styleBouton } from '../../app/styles.js';
import type { ControleurAccueil, EtatAccueil } from './controleur.js';
import type { SaisieEntretien } from './entretien.js';
import { REFERENTIEL_ACCUEIL, type TypeDemande } from './referentiel.js';

const styleChamp: CSSProperties = {
  display: 'block',
  width: '100%',
  boxSizing: 'border-box',
  minHeight: CIBLE_MIN,
  padding: '0 12px',
  border: `1px solid ${COULEURS.bordure}`,
  borderRadius: 8,
  font: 'inherit',
};
const stylePrincipal: CSSProperties = {
  ...styleBouton,
  background: COULEURS.boutonPrincipal,
  borderColor: COULEURS.boutonPrincipal,
  color: COULEURS.blanc,
};
const styleBadge: CSSProperties = {
  display: 'inline-block',
  padding: '2px 8px',
  borderRadius: 8,
  background: COULEURS.ambreFond,
  color: COULEURS.ambre,
  fontSize: 14,
};

export interface PropsVueAccueil {
  etat: EtatAccueil;
  controleur: ControleurAccueil;
  /** Brigitte ou la direction : seuls rôles qui valident l'arbre. */
  peutValider: boolean;
  /** Dossier auquel l'arbre est enregistré ; sans dossier, la validation reste fermée. */
  dossierId?: string | null;
  /** Retour à la saisie après une erreur. */
  onReessayer?: () => void;
  /** Faux sans transcripteur fourni par la coquille : le vocal se dépose alors au dossier (couloir G, point 6), transcrit par le serveur s'il le peut. */
  vocalDisponible?: boolean;
  /** Dossier où déposer le vocal (dossier ouvert, ou créé par l'enregistrement de l'entretien). */
  dossierVocal?: string | null;
}

export const MESSAGE_VOCAL_INDISPONIBLE =
  'Le dépôt d’un vocal n’est pas encore disponible : saisissez l’entretien dans le formulaire. Cabinet MAAT';

function Champ({ id, libelle, erreur, children }: { id: string; libelle: string; erreur?: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <label htmlFor={id} style={{ display: 'block', marginBottom: 4, color: COULEURS.marine }}>
        {libelle}
      </label>
      {children}
      {erreur ? (
        <p id={`${id}-erreur`} role="alert" style={{ color: COULEURS.rouge, margin: '4px 0 0' }}>
          {erreur}
        </p>
      ) : null}
    </div>
  );
}

/** Écran d'accueil par vocal : entretien initial, dépôt du vocal, arbre provisoire en hypothèse à valider. */
export function VueAccueil({ etat, controleur, peutValider, dossierId = null, onReessayer, vocalDisponible = true, dossierVocal = null }: PropsVueAccueil) {
  const { saisie, bilan, phase, arbre, accuse, compteRendu, retour } = etat;
  const saisir = (champ: keyof SaisieEntretien) => (e: { target: { value: string } }) => controleur.saisir(champ, e.target.value);
  const enValidation = phase === 'validation';
  const peutCliquer = peutValider && dossierId !== null && !enValidation;

  return (
    <div>
      <form onSubmit={(e) => e.preventDefault()} aria-label="Entretien d’accueil">
        <Champ
          id="accueil-type"
          libelle={REFERENTIEL_ACCUEIL.questions.type}
          {...(bilan.erreurs.typeDemande ? { erreur: bilan.erreurs.typeDemande } : {})}
        >
          <select id="accueil-type" value={saisie.typeDemande} onChange={saisir('typeDemande')} style={styleChamp}>
            <option value="">Choisir…</option>
            {REFERENTIEL_ACCUEIL.types.map((t: { code: TypeDemande; libelle: string }) => (
              <option key={t.code} value={t.code}>
                {t.libelle}
              </option>
            ))}
          </select>
        </Champ>
        <Champ
          id="accueil-proprietaire"
          libelle={REFERENTIEL_ACCUEIL.questions.proprietaire}
          {...(bilan.erreurs.proprietaireOrigine ? { erreur: bilan.erreurs.proprietaireOrigine } : {})}
        >
          <input
            id="accueil-proprietaire"
            type="text"
            value={saisie.proprietaireOrigine}
            onChange={saisir('proprietaireOrigine')}
            style={styleChamp}
          />
        </Champ>
        <Champ
          id="accueil-naissance"
          libelle={REFERENTIEL_ACCUEIL.questions.naissance}
          {...(bilan.erreurs.dateNaissanceProprietaire ? { erreur: bilan.erreurs.dateNaissanceProprietaire } : {})}
        >
          <input
            id="accueil-naissance"
            type="text"
            inputMode="numeric"
            placeholder="1932 ou 1932-05-04"
            value={saisie.dateNaissanceProprietaire}
            onChange={saisir('dateNaissanceProprietaire')}
            style={styleChamp}
          />
        </Champ>
        {bilan.alerteAncien ? (
          <p role="status" style={{ background: COULEURS.ambreFond, color: COULEURS.ambre, padding: 12, borderRadius: 8 }}>
            {bilan.alerteAncien}
          </p>
        ) : null}

        {/* Couloir G, point 6 : un vrai dépôt du vocal au dossier, à la place de « pas encore disponible ». */}
        {vocalDisponible ? null : <DepotVocal dossierId={dossierVocal ?? dossierId} />}
        {vocalDisponible ? (
          <div style={{ marginBottom: 16 }}>
            <label htmlFor="accueil-vocal" style={{ display: 'block', marginBottom: 4, color: COULEURS.marine }}>
              Enregistrer ou déposer un vocal
            </label>
            <input
              id="accueil-vocal"
              type="file"
              accept="audio/*"
              capture="user"
              disabled={phase === 'transcription' || enValidation}
              style={styleChamp}
              onChange={(e) => {
                const fichier = e.target.files?.[0];
                if (fichier)
                  void fichier.arrayBuffer().then((b) => controleur.deposerVocal(new Uint8Array(b), fichier.type || 'audio/webm'));
              }}
            />
          </div>
        ) : null}
        {dossierId === null ? (
          // « Qui est venu vous voir ? » (décision de Joel) : même donnée et même champ que « Nouveau dossier », quand l'accueil crée le dossier.
          <fieldset style={{ border: 'none', margin: '0 0 16px', padding: 0 }}>
            <legend style={{ marginBottom: 4, color: COULEURS.marine }}>{PREMIER_CONTACT.question}</legend>
            {PREMIER_CONTACT.options.map((o) => (
              <label key={o.code} style={{ display: 'flex', alignItems: 'center', gap: 8, minHeight: CIBLE_MIN, cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="accueil-venu-par"
                  value={o.code}
                  checked={saisie.venuPar === o.code}
                  onChange={() => controleur.saisir('venuPar', o.code)}
                  style={{ width: 22, minHeight: CIBLE_MIN, margin: 0 }}
                />
                {o.libelle}
              </label>
            ))}
          </fieldset>
        ) : null}
        {/* D16 (26/10) : l'entretien s'enregistre sans vocal ; bouton visible dès l'état initial, les manques sont dits au clic. */}
        {etat.enregistrement ? null : (
          <button
            type="button"
            disabled={phase === 'enregistrement'}
            aria-busy={phase === 'enregistrement'}
            onClick={() => void controleur.enregistrerEntretien(dossierId)}
            style={{ ...stylePrincipal, minHeight: CIBLE_MIN }}
          >
            {phase === 'enregistrement' ? REFERENTIEL_ACCUEIL.enregistrement.enCours : REFERENTIEL_ACCUEIL.enregistrement.bouton}
          </button>
        )}
      </form>

      {etat.enregistrement ? (
        <div role="status" style={{ margin: '12px 0', color: COULEURS.marine }}>
          <p style={{ margin: '0 0 8px' }}>
            {etat.enregistrement.reference
              ? REFERENTIEL_ACCUEIL.enregistrement.dossierCree.replace('{reference}', etat.enregistrement.reference)
              : REFERENTIEL_ACCUEIL.enregistrement.auDossier}
          </p>
          <Lien
            vers={`/dossier?dossier=${encodeURIComponent(etat.enregistrement.dossierId)}`}
            enfants={REFERENTIEL_ACCUEIL.enregistrement.lienDossier}
            style={{ display: 'inline-flex', alignItems: 'center', minHeight: CIBLE_MIN }}
          />
        </div>
      ) : null}

      {phase === 'transcription' ? <p role="status">Transcription du vocal en cours…</p> : null}

      {phase === 'erreur' ? (
        <div role="alert">
          <p style={{ color: COULEURS.rouge }}>{retour}</p>
          <button type="button" onClick={onReessayer} style={styleBouton}>
            Réessayer
          </button>
        </div>
      ) : retour ? (
        <p role="status" style={{ color: COULEURS.marine }}>
          {retour}
        </p>
      ) : null}

      {(phase === 'arbre' || enValidation) && arbre ? (
        <section aria-label="Arbre proposé">
          {accuse ? <p role="status">{accuse}</p> : null}
          {arbre.statut === 'enregistre' ? (
            <h2 style={{ color: COULEURS.marine, fontSize: 20 }}>Arbre enregistré au dossier, en hypothèse</h2>
          ) : null}
          {arbre.personnes.length > 0 ? (
            <ul style={{ listStyle: 'none', padding: 0 }}>
              {arbre.personnes.map((p) => (
                <li key={p.ref} style={{ minHeight: CIBLE_MIN, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span>{p.nom}</span>
                  {p.hypothese ? <span style={styleBadge}>Hypothèse</span> : null}
                </li>
              ))}
            </ul>
          ) : null}
          {arbre.statut === 'hypothese' && arbre.personnes.length > 0 ? (
            <>
              <button
                type="button"
                disabled={!peutCliquer}
                aria-busy={enValidation}
                onClick={() => dossierId && void controleur.validerArbre(dossierId)}
                style={peutCliquer ? stylePrincipal : styleBouton}
              >
                {enValidation ? 'Validation en cours…' : 'Valider l’arbre'}
              </button>
              {enValidation ? <p role="status">Enregistrement de l’arbre au dossier, en hypothèse…</p> : null}
              {!peutValider ? <p>Seule Brigitte, ou la direction du Cabinet MAAT, peut valider cet arbre.</p> : null}
            </>
          ) : null}
        </section>
      ) : null}

      {compteRendu ? (
        <section aria-label="Compte rendu">
          <h2 style={{ color: COULEURS.marine, fontSize: 20 }}>
            Compte rendu <span style={styleBadge}>{compteRendu.statut === 'a_verifier' ? 'À vérifier' : 'Relu'}</span>
          </h2>
          <pre style={{ whiteSpace: 'pre-wrap', font: 'inherit' }}>{compteRendu.texte}</pre>
        </section>
      ) : null}
    </div>
  );
}
